import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Paper,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Modal,
    Rating,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Divider,
    Stack,
    TablePagination
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Visibility,
    ExpandMore,
    Star,
    StarBorder,
    Feedback,
    Restaurant,
    ShoppingBag,
    EmojiEmotions,
    CleaningServices,
    Schedule,
    CreditCard,
    Assignment,
    Person,
    Phone,
    LocalOffer,
    QrCode,
    FilterList,
    Clear,
    Search,
    FilterAlt,
    Fastfood
} from '@mui/icons-material';
import { useNotification } from '../contexts/NotificationContext';
import { getAllFeedbacks } from "../services/feedBackService"
import outletService from "../services/outletService"
import {feedback_to_testimonial} from "../services/testimonialService"

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: '80%', md: 700 },
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const feedbackTypes = [
    { value: 'TAKEAWAY', label: 'Takeaway' },
    { value: 'DELIVERY', label: 'Delivery' },
    { value: 'RESERVATION', label: 'Reservation' },
    { value: 'CATERING', label: 'Catering' }
];

const ratingCategories = [
    { key: 'overall_rating', label: 'Overall Rating', icon: Star, required: true },
    { key: 'food_quality', label: 'Food Quality', icon: Restaurant },
    { key: 'packaging', label: 'Packaging', icon: ShoppingBag },
    { key: 'staff_courtesy', label: 'Staff Courtesy', icon: EmojiEmotions },
    { key: 'hygiene', label: 'Hygiene', icon: CleaningServices },
    { key: 'delivery_timing', label: 'Delivery Timing', icon: Schedule },
    { key: 'ease_of_booking', label: 'Ease of Booking', icon: CreditCard },
    { key: 'presentation', label: 'Presentation', icon: Restaurant },
    { key: 'professionalism', label: 'Professionalism', icon: Assignment }
];

const FeedBackManagement = () => {
    const { showSuccess, showError } = useNotification();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [feedbackData, setFeedbackData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState({ type: 'create', value: false });
    const [validationErrors, setValidationErrors] = useState({});
    const [viewMode, setViewMode] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        start_date: '',
        end_date: '',
        rating: '',
        feedback_type: ''
    });
    const [appliedFilters, setAppliedFilters] = useState({
        search: '',
        start_date: '',
        end_date: '',
        rating: '',
        feedback_type: '',
        outlet_id: localStorage.getItem("admin_outlet_id") ? JSON.parse(localStorage.getItem("admin_outlet_id")) : 'all'
    });
    const [outlets, setOutlets] = useState([])
    const [selectedOutlet,setSelectedOutlet] = useState('all')
    const [showFilters, setShowFilters] = useState(false);
    const [reloadForAdmin,setReloadForAdmin] = useState(false)

    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const initialFormData = {
        id: null,
        guest_id: '',
        user_session_id: '',
        feedback_type: '',
        overall_rating: 0,
        comments: '',
        food_quality: 0,
        packaging: 0,
        staff_courtesy: 0,
        hygiene: 0,
        delivery_timing: 0,
        ease_of_booking: 0,
        presentation: 0,
        professionalism: 0,
        created_at: '',
        updated_at: '',
        order_number: '',
        reservation_number: '',
        catering_request_number: '',
        customer_name: '',
        customer_phone: '',
        outlet_name: ''
    };

    const [formData, setFormData] = useState(initialFormData);
    const [expandedRatings, setExpandedRatings] = useState(false);


    useEffect(() => {
        fetchFeedbacks();
        fetchOutlets()
        if(localStorage.getItem("admin_outlet_id")){
            setSelectedOutlet(JSON.parse(localStorage.getItem("admin_outlet_id")))
            setReloadForAdmin(true)
        }
        else{
            setSelectedOutlet('all')
        }
    }, []);

    // Apply filters whenever feedbackData or appliedFilters change
    useEffect(() => {
        applyAllFilters();
    }, [feedbackData, appliedFilters]);

    useEffect(()=>{
        handleApplyFilters()
    },[reloadForAdmin])

    const fetchOutlets = async () => {
        try {
            const response = await outletService.getOutlets()
            // const outletsData = await response.json();
            setOutlets(response);

        } catch (error) {
            console.error('Error fetching outlets:', error);
        }
    };

    const fetchFeedbacks = async () => {
        setLoading(true);
        try {
            // Prepare query parameters for backend filters
            const params = {};

            if (appliedFilters.start_date) {
                params.start_date = appliedFilters.start_date;
            }
            if (appliedFilters.end_date) {
                params.end_date = appliedFilters.end_date;
            }
            if (appliedFilters.rating) {
                params.rating = appliedFilters.rating;
            }
            if (appliedFilters.feedback_type) {
                params.feedback_type = appliedFilters.feedback_type;
            }
            if(appliedFilters.outlet_id && appliedFilters.outlet_id !== 'all'){
                params.outlet_id = appliedFilters.outlet_id
            }

            // console.log('Fetching with params:', params);

            const apiResponse = await getAllFeedbacks(params);
            setFeedbackData(apiResponse.data || []);
        } catch (error) {
            console.error("Error fetching feedbacks =>", error);
            showError("Failed to fetch feedbacks");
        } finally {
            setLoading(false);
        }
    };

    const applyAllFilters = () => {
        let data = [...feedbackData];

        // Apply search filter (frontend)
        if (appliedFilters.search && appliedFilters.search.trim() !== '') {
            const searchLower = appliedFilters.search.toLowerCase().trim();
            data = data.filter(item =>
                item.customer_name?.toLowerCase().includes(searchLower) ||
                item.customer_phone?.includes(appliedFilters.search) ||
                item.guest_id?.toLowerCase().includes(searchLower) ||
                item.order_number?.toLowerCase().includes(searchLower) ||
                item.reservation_number?.toLowerCase().includes(searchLower) ||
                item.catering_request_number?.toLowerCase().includes(searchLower)
            );
        }

        // Note: Date, rating, and feedback_type filters are already applied on the backend
        // We just need to ensure we're using the filtered data from the API

        setFilteredData(data);
        setPage(0);
    };

    const handleApplyFilters = () => {
        // Get the current filter values directly
        const currentFilters = {
            search: filters.search,
            start_date: filters.start_date,
            end_date: filters.end_date,
            rating: filters.rating,
            feedback_type: filters.feedback_type,
            outlet_id : selectedOutlet
        };

        // console.log('Applying filters:', currentFilters);

        // Validate date range
        if (currentFilters.start_date && currentFilters.end_date) {
            const start = new Date(currentFilters.start_date);
            const end = new Date(currentFilters.end_date);
            // Set time to 00:00:00 for proper date comparison
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);

            if (start > end) {
                showError('End date must be on or after the start date');
                return;
            }
        }

        // Update applied filters with current filter values
        setAppliedFilters(currentFilters);

        // Fetch data with new filters
        fetchFeedbacksWithParams(currentFilters);
        setPage(0);
    };

    // New function to fetch with specific params
    const fetchFeedbacksWithParams = async (filterParams) => {
        setLoading(true);
        try {
            const params = {};

            if (filterParams.start_date) {
                params.start_date = filterParams.start_date;
            }
            if (filterParams.end_date) {
                params.end_date = filterParams.end_date;
            }
            if (filterParams.rating) {
                params.rating = filterParams.rating;
            }
            if (filterParams.feedback_type) {
                params.feedback_type = filterParams.feedback_type;
            }
            if(filterParams.outlet_id && filterParams.outlet_id !== 'all'){
                params.outlet_id = filterParams.outlet_id;
            }

            // console.log('Fetching with params:', params);

            const apiResponse = await getAllFeedbacks(params);
            setFeedbackData(apiResponse.data || []);
        } catch (error) {
            console.error("Error fetching feedbacks =>", error);
            showError("Failed to fetch feedbacks");
        } finally {
            setLoading(false);
        }
    };

  
    const handleFilterChange = (e) => {
        const { name, value } = e.target;

        // If start_date is being set, validate it's not after end_date
        if (name === 'start_date' && value && filters.end_date) {
            const start = new Date(value);
            const end = new Date(filters.end_date);
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);

            if (start > end) {
                showError('Start date cannot be after end date');
                // Don't update the state if invalid
                return;
            }
        }

        // If end_date is being set, validate it's not before start_date
        if (name === 'end_date' && value && filters.start_date) {
            const start = new Date(filters.start_date);
            const end = new Date(value);
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);

            if (end < start) {
                showError('End date must be on or after the start date');
                // Don't update the state if invalid
                return;
            }
        }

        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilters = () => {
        const emptyFilters = {
            search: '',
            start_date: '',
            end_date: '',
            rating: '',
            feedback_type: ''
        };
        setFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        // Fetch all data without filters
        fetchFeedbacksWithParams(emptyFilters);
        setPage(0);
    };

    const handleSearchChange = (e) => {
        const { value } = e.target;
        setFilters(prev => ({
            ...prev,
            search: value
        }));
        // Update applied filters to trigger search
        setAppliedFilters(prev => ({
            ...prev,
            search: value
        }));
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleModalChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleRatingChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.guest_id || !formData.guest_id.trim()) {
            errors.guest_id = 'Guest ID is required';
        }
        if (!formData.user_session_id) {
            errors.user_session_id = 'User Session ID is required';
        }
        if (!formData.feedback_type) {
            errors.feedback_type = 'Feedback type is required';
        }
        if (!formData.overall_rating || formData.overall_rating === 0) {
            errors.overall_rating = 'Overall rating is required';
        }
        if (!formData.customer_name || !formData.customer_name.trim()) {
            errors.customer_name = 'Customer name is required';
        }
        if (!formData.customer_phone || !formData.customer_phone.trim()) {
            errors.customer_phone = 'Customer phone is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const submitFeedback = async (action, id) => {
        if (!validateForm()) {
            showError('Please fill in all required fields');
            return;
        }

        try {
            if (action === 'create') {
                // console.log('Creating feedback:', formData);
                showSuccess('Feedback created successfully');
            } else {
                // console.log('Updating feedback:', formData);
                showSuccess('Feedback updated successfully');
            }

            await fetchFeedbacks();
            setModalOpen({ ...modalOpen, value: false });
            setFormData(initialFormData);
            setValidationErrors({});
            setViewMode(false);
        } catch (error) {
            console.error("Error saving feedback =>", error);
            showError("Unable to save feedback");
        }
    };

    const viewFeedback = (feedback) => {
        setFormData(feedback);
        setViewMode(true);
        setModalOpen({ type: 'view', value: true });
    };

    const editSpecificFeedback = async (id) => {
        try {
            const feedback = feedbackData.find(f => f.id === id);
            if (feedback) {
                setFormData(feedback);
                setViewMode(false);
                setModalOpen({ type: 'edit', value: true });
            }
        } catch (error) {
            console.error("Error fetching feedback for edit =>", error);
            showError("Failed to fetch feedback details");
        }
    };

    const deleteFeedback = async (id) => {
        if (window.confirm('Are you sure you want to delete this feedback?')) {
            try {
                // console.log('Deleting feedback:', id);
                showSuccess('Feedback deleted successfully');
                await fetchFeedbacks();
            } catch (error) {
                console.error("Error deleting feedback =>", error);
                showError("Failed to delete feedback");
            }
        }
    };

    const getFeedbackTypeColor = (type) => {
        const colors = {
            TAKEAWAY: '#FF9800',
            DELIVERY: '#2196F3',
            RESERVATION: '#9C27B0',
            CATERING: '#F44336'
        };
        return colors[type] || '#757575';
    };

    const getFeedbackTypeLabel = (type) => {
        const labels = {
            TAKEAWAY: 'Takeaway',
            DELIVERY: 'Delivery',
            RESERVATION: 'Reservation',
            CATERING: 'Catering'
        };
        return labels[type] || type;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const parts = dateString.split(' ');
            const dateParts = parts[0].split('-');
            const timeParts = parts[1]?.split(':') || ['00', '00'];
            const date = new Date(
                parseInt(dateParts[2]),
                parseInt(dateParts[1]) - 1,
                parseInt(dateParts[0]),
                parseInt(timeParts[0]),
                parseInt(timeParts[1])
            );
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const renderRatingStars = (rating, size = 'small') => {
        return (
            <Rating
                value={rating || 0}
                readOnly
                precision={0.5}
                size={size}
                emptyIcon={<StarBorder style={{ opacity: 0.55 }} />}
            />
        );
    };

    const renderRatingInput = (category) => {
        const { key, label, icon: Icon, required } = category;
        const value = formData[key] || 0;

        const isSpecificRating = ['delivery_timing', 'ease_of_booking', 'presentation', 'professionalism'].includes(key);

        if (isSpecificRating) {
            if (formData.feedback_type === 'DELIVERY' && !['delivery_timing'].includes(key)) return null;
            if (formData.feedback_type === 'RESERVATION' && !['ease_of_booking'].includes(key)) return null;
            if (formData.feedback_type === 'CATERING' && !['professionalism'].includes(key)) return null;
            if (formData.feedback_type === 'TAKEAWAY' && !['packaging', 'food_quality'].includes(key)) return null;
            if (!formData.feedback_type) return null;
        }

        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: '1px solid #eee'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon sx={{ color: '#FFD700' }} />
                    <Typography variant="body2">
                        {label}
                        {required && <span style={{ color: 'red' }}>*</span>}
                    </Typography>
                </Box>
                <Rating
                    name={key}
                    value={value}
                    onChange={(event, newValue) => handleRatingChange(key, newValue)}
                    precision={0.5}
                    size="medium"
                    readOnly={viewMode}
                />
            </Box>
        );
    };

    const getReferenceNumber = (feedback) => {
        if (feedback.feedback_type === 'DELIVERY') {
            return feedback.order_number;
        } else if (feedback.feedback_type === 'RESERVATION') {
            return feedback.reservation_number;
        } else if (feedback.feedback_type === 'CATERING') {
            return feedback.catering_request_number;
        } else if (feedback.feedback_type === 'TAKEAWAY') {
            return feedback.order_number;
        }
        return null;
    };

    const getReferenceLabel = (feedback) => {
        if (feedback.feedback_type === 'DELIVERY') {
            return 'Order #';
        } else if (feedback.feedback_type === 'RESERVATION') {
            return 'Reservation #';
        } else if (feedback.feedback_type === 'CATERING') {
            return 'Catering Request #';
        } else if (feedback.feedback_type === 'TAKEAWAY') {
            return 'Order #';
        }
        return 'Reference #';
    };

    const displayData = filteredData;

    // Paginated data
    const paginatedData = displayData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Check if any filters are applied (excluding search which is handled separately)
    const hasActiveFilters = appliedFilters.start_date ||
        appliedFilters.end_date ||
        appliedFilters.rating ||
        appliedFilters.feedback_type;

    // Get min date for end date (start date or today)
    const getMinEndDate = () => {
        if (filters.start_date) {
            return filters.start_date;
        }
        const today = new Date().toISOString().split('T')[0];
        return today;
    };


    const handleAddToTestimonial = async(id) => {
        // console.log("FeedBack ID => ", id)
        let payload = {
            "feedback_id":parseInt(id)
        }
        try{
            const response = await feedback_to_testimonial(payload)
            showSuccess("Feedback Added to Testimonial Successfully");
        }
        catch(error){   
            showError(error?.message)
    
        }
    }

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{
                mb: 3,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 2, sm: 0 }
            }}>
                <Box>
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        fontWeight="bold"
                        gutterBottom
                    >
                        Feedback Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage customer feedback and ratings
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>

                    <Button
                        variant="outlined"
                        startIcon={<FilterList />}
                        onClick={() => setShowFilters(!showFilters)}
                        size={isMobile ? "small" : "medium"}
                    >
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                    {(hasActiveFilters || filters.search) && (
                        <Button
                            size="small"
                            color="error"
                            onClick={clearFilters}
                            startIcon={<Clear />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Filters Section */}
            {showFilters && (
                <Card sx={{ mb: 3, p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search"
                                name="search"
                                placeholder="Search by customer, phone, reference..."
                                value={filters.search}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Select Outlet</InputLabel>
                                <Select
                                    name="outlet"
                                    disabled = {JSON.parse(localStorage.getItem("admin_outlet_id"))}
                                    value={selectedOutlet}
                                    onChange={(e)=> setSelectedOutlet(e.target.value)}
                                    label="Select Outlet"
                                >
                                    <MenuItem value="all">All Outlets</MenuItem>
                                    {outlets.map(outlet => (
                                        <MenuItem key={outlet.id} value={outlet.id}>
                                            {outlet.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Start Date"
                                name="start_date"
                                type="date"
                                value={filters.start_date}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="End Date"
                                name="end_date"
                                type="date"
                                value={filters.end_date}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                                disabled={!filters.start_date}
                                inputProps={{
                                    min: getMinEndDate()
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Rating</InputLabel>
                                <Select
                                    name="rating"
                                    value={filters.rating}
                                    onChange={handleFilterChange}
                                    label="Rating"
                                >
                                    <MenuItem value="">All Ratings</MenuItem>
                                    {[1, 2, 3, 4, 5].map(rating => (
                                        <MenuItem key={rating} value={rating}>
                                            {rating} Star{rating > 1 ? 's' : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Feedback Type</InputLabel>
                                <Select
                                    name="feedback_type"
                                    value={filters.feedback_type}
                                    onChange={handleFilterChange}
                                    label="Feedback Type"
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    {feedbackTypes.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<FilterAlt />}
                                onClick={handleApplyFilters}
                                size="small"
                                sx={{ height: '40px' }}
                            >
                                Apply
                            </Button>
                        </Grid>
                    </Grid>
                </Card>
            )}

            {/* Results Count */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {paginatedData.length} of {displayData.length} feedbacks
                    {hasActiveFilters && ' (filtered)'}
                    {appliedFilters.search && appliedFilters.search.trim() !== '' && ` (search: "${appliedFilters.search}")`}
                </Typography>
            </Box>

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Empty State */}
            {!loading && displayData.length === 0 && (
                <Card sx={{
                    p: 3,
                    pb: 2,
                    textAlign: 'center',
                    bgcolor: '#fff',
                    boxShadow: 1
                }}>
                    <CardContent>
                        <Feedback sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            {feedbackData.length === 0 ? 'No Feedbacks Available' : 'No results found'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {feedbackData.length === 0 ? 'Create feedback to get started' : 'Try adjusting your filters'}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* Feedback Table */}
            {!loading && displayData.length > 0 && (
                <Card>
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': {
                                height: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                borderRadius: '4px',
                            },
                        }}
                    >
                        <Table sx={{ minWidth: 700 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Outlet</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Overall Rating</TableCell>
                                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                                        Comments
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>
                                        Date
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.map((feedback, index) => (
                                    <TableRow key={feedback.id} hover>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {feedback.customer_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {feedback.customer_phone}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {feedback.outlet_name}
                                                </Typography>

                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getFeedbackTypeLabel(feedback.feedback_type)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getFeedbackTypeColor(feedback.feedback_type),
                                                    color: '#fff',
                                                    fontWeight: 500
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {getReferenceNumber(feedback) || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {renderRatingStars(feedback.overall_rating)}
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {feedback.comments || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {formatDate(feedback.created_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        color="info"
                                                        onClick={() => viewFeedback(feedback)}
                                                    >
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={displayData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Rows per page:"
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} of ${count}`
                        }
                        sx={{
                            borderTop: '1px solid #e0e0e0',
                            '& .MuiTablePagination-select': {
                                marginRight: '8px'
                            }
                        }}
                    />
                </Card>
            )}

            {/* Modal for Create/Edit/View */}
            <Modal
                open={modalOpen.value}
                onClose={() => {
                    setModalOpen({ ...modalOpen, value: false });
                    setFormData(initialFormData);
                    setValidationErrors({});
                    setViewMode(false);
                }}
                aria-labelledby="feedback-modal-title"
            >
                <Box sx={style}>
                    <Typography
                        id="feedback-modal-title"
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            mb: 3,
                            textAlign: 'center'
                        }}
                    >
                        {viewMode ? 'Feedback Details' :
                            modalOpen.type === 'create' ? 'Add New Feedback' : 'Edit Feedback'}
                    </Typography>

                    {viewMode ? (
                        // View Mode - Display as non-editable
                        <Box sx={{ mb: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.customer_name || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Customer Phone</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.customer_phone || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="caption" color="text.secondary">Feedback Type</Typography>
                                        <Chip
                                            label={getFeedbackTypeLabel(formData.feedback_type)}
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                width: '150px',
                                                backgroundColor: getFeedbackTypeColor(formData.feedback_type),
                                                color: '#fff',
                                                fontWeight: 500
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                {formData.feedback_type && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">
                                            {getReferenceLabel({ feedback_type: formData.feedback_type })}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {formData.feedback_type === 'DELIVERY' ? formData.order_number :
                                                formData.feedback_type === 'TAKEAWAY' ? formData.order_number :
                                                    formData.feedback_type === 'RESERVATION' ? formData.reservation_number :
                                                        formData.catering_request_number || '-'}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Outlet Name</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.outlet_name || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Overall Rating</Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        {renderRatingStars(formData.overall_rating, 'medium')}
                                    </Box>
                                </Grid>
                                {formData.comments && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Comments</Typography>
                                        <Typography variant="body1">{formData.comments}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Accordion expanded={expandedRatings} onChange={() => setExpandedRatings(!expandedRatings)}>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="subtitle1" fontWeight={500}>
                                                Detailed Ratings
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                                                {ratingCategories.map(category => {
                                                    const { key, label, icon: Icon } = category;
                                                    const isSpecificRating = ['delivery_timing', 'ease_of_booking', 'presentation', 'professionalism'].includes(key);

                                                    if (isSpecificRating) {
                                                        if (formData.feedback_type === 'DELIVERY' && !['delivery_timing'].includes(key)) return null;
                                                        if (formData.feedback_type === 'RESERVATION' && !['ease_of_booking'].includes(key)) return null;
                                                        if (formData.feedback_type === 'CATERING' && !['professionalism'].includes(key)) return null;
                                                        if (formData.feedback_type === 'TAKEAWAY' && !['packaging', 'food_quality'].includes(key)) return null;
                                                        if (!formData.feedback_type) return null;
                                                    }

                                                    const value = formData[key] || 0;
                                                    if (value === 0 && key !== 'overall_rating') return null;

                                                    return (
                                                        <Box key={key} sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            py: 1,
                                                            borderBottom: '1px solid #eee'
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Icon sx={{ color: '#FFD700' }} />
                                                                <Typography variant="body2">{label}</Typography>
                                                            </Box>
                                                            {renderRatingStars(value, 'medium')}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: '#f5f5f5',
                                        borderRadius: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 1
                                    }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Created: {formatDate(formData.created_at)}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : (
                        // Edit/Create Mode - Editable fields
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Customer Name"
                                    name="customer_name"
                                    value={formData.customer_name || ''}
                                    onChange={handleModalChange}
                                    error={!!validationErrors.customer_name}
                                    helperText={validationErrors.customer_name}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Customer Phone"
                                    name="customer_phone"
                                    value={formData.customer_phone || ''}
                                    onChange={handleModalChange}
                                    error={!!validationErrors.customer_phone}
                                    helperText={validationErrors.customer_phone}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Guest ID"
                                    name="guest_id"
                                    value={formData.guest_id || ''}
                                    onChange={handleModalChange}
                                    error={!!validationErrors.guest_id}
                                    helperText={validationErrors.guest_id}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="User Session ID"
                                    name="user_session_id"
                                    type="number"
                                    value={formData.user_session_id || ''}
                                    onChange={handleModalChange}
                                    error={!!validationErrors.user_session_id}
                                    helperText={validationErrors.user_session_id}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!validationErrors.feedback_type}>
                                    <InputLabel>Feedback Type *</InputLabel>
                                    <Select
                                        name="feedback_type"
                                        value={formData.feedback_type || ''}
                                        onChange={handleModalChange}
                                        label="Feedback Type *"
                                    >
                                        {feedbackTypes.map(type => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {validationErrors.feedback_type && (
                                        <Typography variant="caption" color="error">
                                            {validationErrors.feedback_type}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>
                            {formData.feedback_type && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={getReferenceLabel({ feedback_type: formData.feedback_type })}
                                        name={
                                            formData.feedback_type === 'DELIVERY' || formData.feedback_type === 'TAKEAWAY' ? 'order_number' :
                                                formData.feedback_type === 'RESERVATION' ? 'reservation_number' :
                                                    'catering_request_number'
                                        }
                                        value={
                                            formData.feedback_type === 'DELIVERY' || formData.feedback_type === 'TAKEAWAY' ? formData.order_number || '' :
                                                formData.feedback_type === 'RESERVATION' ? formData.reservation_number || '' :
                                                    formData.catering_request_number || ''
                                        }
                                        onChange={handleModalChange}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 1
                                }}>
                                    <Typography variant="body1" fontWeight={500}>
                                        Overall Rating *
                                    </Typography>
                                    <Rating
                                        name="overall_rating"
                                        value={formData.overall_rating || 0}
                                        onChange={(event, newValue) => handleRatingChange('overall_rating', newValue)}
                                        precision={0.5}
                                        size="large"
                                    />
                                </Box>
                                {validationErrors.overall_rating && (
                                    <Typography variant="caption" color="error">
                                        {validationErrors.overall_rating}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Comments"
                                    name="comments"
                                    multiline
                                    rows={3}
                                    value={formData.comments || ''}
                                    onChange={handleModalChange}
                                    placeholder="Share your feedback experience..."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Accordion
                                    expanded={expandedRatings}
                                    onChange={() => setExpandedRatings(!expandedRatings)}
                                >
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" fontWeight={500}>
                                            Detailed Ratings
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            (Optional)
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                                            {ratingCategories.map(category => renderRatingInput(category))}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        </Grid>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                     <Button
                            variant="outlined"
                            onClick={() => {
                                handleAddToTestimonial(formData.id)
                            }}
                        >
                            Add to Testimonial
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setModalOpen({ ...modalOpen, value: false });
                                setFormData(initialFormData);
                                setValidationErrors({});
                                setViewMode(false);
                            }}
                        >
                            {viewMode ? 'Close' : 'Cancel'}
                        </Button>
                        {!viewMode && (
                            <Button
                                variant="contained"
                                onClick={() => submitFeedback(
                                    modalOpen.type,
                                    modalOpen.type === 'edit' ? formData.id : null
                                )}
                            >
                                {modalOpen.type === 'create' ? 'Create Feedback' : 'Update Feedback'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default FeedBackManagement;